import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { authFetch, getLocalApiUrl } from '../../config/api'
import { 
  UserCircleIcon,
  PencilIcon,
  KeyIcon,
  BellIcon,
  ShieldCheckIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

function Profile() {
  const { user: authUser } = useAuth()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pw, setPw] = useState({ current:'', next:'', confirm:'' })

  // Load user data from API
  useEffect(() => {
    if (authUser?.username) {
      loadUserData()
    }
  }, [authUser])

  const loadUserData = async () => {
    try {
      setLoading(true)

      // Fetch reference data and user list in parallel
      const [usersRes, refRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/users')),
        authFetch(getLocalApiUrl('/api/admin/reference-data'))
      ])
      const usersResult = await usersRes.json()
      const refResult = await refRes.json()

      const rawRef = refResult.success && refResult.data && typeof refResult.data === 'object' ? refResult.data : {}
      const refData = {
        teams: Array.isArray(rawRef.teams) ? rawRef.teams : [],
        departments: Array.isArray(rawRef.departments) ? rawRef.departments : [],
        locations: Array.isArray(rawRef.locations) ? rawRef.locations : [],
        titles: rawRef.titles && typeof rawRef.titles === 'object' ? rawRef.titles : {},
      }

      if (usersResult.success) {
        // Find current user
        const dbUser = (usersResult.data || []).find(u => u.username === authUser.username)
        if (dbUser) {
          // Split name into first and last
          const nameParts = (dbUser.name || authUser.name || '').split(' ')
          const firstName = nameParts[0] || authUser.username
          const lastName = nameParts.slice(1).join(' ') || ''

          const roleKey = authUser.role || dbUser.role || 'viewer'

          // Combine DB data with auth context and reference data
          const enrichedUser = {
            id: authUser.username,
            username: authUser.username,
            email: dbUser.email || `${authUser.username}@company.com`,
            firstName: firstName,
            lastName: lastName,
            role: roleKey.toUpperCase(),
            team: dbUser.team || (refData.teams[0] || ''),
            phone: dbUser.phone || '+61 412 XXX XXX',
            department: dbUser.department || (refData.departments[0] || ''),
            title: dbUser.title || refData.titles?.[roleKey] || 'User',
            location: dbUser.location || (refData.locations[0] || ''),
            timezone: 'Australia/Sydney',
            language: 'English (AU)',
            dateFormat: 'DD/MM/YYYY',
            lastKycDate: '2025-06-15T10:00:00Z',
            lastKycDocument: 'Driver Licence',
            faceprintConfidence: 92,
            membershipDate: dbUser.created_at || '2023-02-10T00:00:00Z',
            lastLogin: null,
            createdAt: dbUser.created_at || new Date().toISOString(),
            profilePicture: null,
            twoFactorEnabled: false,
            emailNotifications: true,
            smsNotifications: false,
            permissions: authUser.permissions || []
          }

          setUser(enrichedUser)
          setEditForm({
            firstName: enrichedUser.firstName,
            lastName: enrichedUser.lastName,
            phone: enrichedUser.phone,
            department: enrichedUser.department,
            title: enrichedUser.title,
            location: enrichedUser.location,
            timezone: enrichedUser.timezone,
            language: enrichedUser.language,
            dateFormat: enrichedUser.dateFormat
          })
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      // Update name in database
      const fullName = `${editForm.firstName} ${editForm.lastName}`.trim()
      const response = await authFetch(getLocalApiUrl(`/api/admin/users/${encodeURIComponent(authUser.username)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: fullName,
          email: user.email,
          phone: editForm.phone
        })
      })
      
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to update profile')
      }
      
      // Update local state with all form data (including mock fields)
      setUser({ ...user, ...editForm, name: fullName })
      setIsEditing(false)
      alert('Profile updated successfully')
    } catch (err) {
      console.error('Error saving profile:', err)
      alert('Failed to update profile: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleNotificationChange = (type, value) => {
    setUser({ ...user, [type]: value })
    // TODO: Save to backend when notifications are implemented
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString('en-AU', { 
      timeZone: 'Australia/Sydney',
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-yellow-800 font-medium mb-2">User Not Found</h3>
        <p className="text-yellow-600 text-sm">Could not load your profile information.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Profile</h1>
        <p className="text-gray-600">
          Manage your account settings, preferences, and security options.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="btn-secondary"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
                <UserCircleIcon className="w-12 h-12 text-gray-500" />
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="text-gray-600">{user.title}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.department}
                    onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.department}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{user.location}</p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Preferences</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={user.timezone}
                  onChange={(e) => setUser({ ...user, timezone: e.target.value })}
                  className="select-field"
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={user.language}
                  onChange={(e) => setUser({ ...user, language: e.target.value })}
                  className="select-field"
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Format
                </label>
                <select
                  value={user.dateFormat}
                  onChange={(e) => setUser({ ...user, dateFormat: e.target.value })}
                  className="select-field"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Notification Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive alerts and updates via email</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.emailNotifications}
                    onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DevicePhoneMobileIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                    <p className="text-sm text-gray-500">Receive critical alerts via SMS</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.smsNotifications}
                    onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <p className="text-sm text-gray-900">{user.role}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Membership Date</label>
                <p className="text-sm text-gray-900">{formatTimestamp(user.membershipDate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Team</label>
                <p className="text-sm text-gray-900">{user.team.replace('_', ' ')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Member Since</label>
                <p className="text-sm text-gray-900">{formatTimestamp(user.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Login</label>
                <p className="text-sm text-gray-900">{formatTimestamp(user.lastLogin)}</p>
              </div>
            </div>
          </div>

          {/* KYC Snapshot */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">KYC Snapshot</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 text-sm">
                <div className="text-gray-600">Last KYC Date</div>
                <div className="text-gray-900">{formatTimestamp(user.lastKycDate)}</div>
              </div>
              <div className="grid grid-cols-2 text-sm">
                <div className="text-gray-600">Last KYC Document</div>
                <div className="text-gray-900">{user.lastKycDocument}</div>
              </div>
              <div className="grid grid-cols-2 text-sm">
                <div className="text-gray-600">Faceprint Confidence</div>
                <div className="text-gray-900">{user.faceprintConfidence}%</div>
              </div>
              {/* Rekyc hint */}
              <div className="pt-2 border-t text-xs text-gray-600">
                {(() => {
                  const monthsSince = Math.max(0, Math.floor((Date.now() - new Date(user.lastKycDate).getTime()) / (1000*60*60*24*30)))
                  return monthsSince >= 12 ? 'Re-KYC recommended (last KYC older than 12 months)' : 'KYC is recent'
                })()}
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Security</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-sm text-gray-900">Two-Factor Auth</span>
                </div>
                <span className="text-sm text-green-600 font-medium">Enabled</span>
              </div>
              
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-full btn-secondary text-left"
              >
                <KeyIcon className="w-4 h-4 mr-2" />
                Change Password
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
            
            <div className="space-y-3">
              <p className="text-sm text-gray-500 italic">Activity tracking coming soon...</p>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Permissions</h2>
            
            <div className="space-y-2">
              {user.permissions && user.permissions.length > 0 ? (
                user.permissions.map((permission) => (
                  <div key={permission} className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-gray-900">
                      {permission.replace('_', ' ').replace(':', ' → ')}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No permissions assigned</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input type="password" className="input-field" value={pw.current} onChange={(e)=>setPw(p=>({...p, current:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input type="password" className="input-field" value={pw.next} onChange={(e)=>setPw(p=>({...p, next:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input type="password" className="input-field" value={pw.confirm} onChange={(e)=>setPw(p=>({...p, confirm:e.target.value}))} />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowChangePassword(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button className="btn-primary" onClick={async()=>{ try { if (!pw.next || pw.next!==pw.confirm) return alert('Passwords do not match'); const r = await authFetch(getLocalApiUrl('/api/account/change-password'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ current_password: pw.current, new_password: pw.next }) }); const j = await r.json(); if (!r.ok || !j.success) return alert(j.error || 'Failed'); alert('Password changed'); setShowChangePassword(false); setPw({ current:'', next:'', confirm:'' }) } catch(e){ alert('Failed: '+e.message) } }}>
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile